// packages/web/src/api/hours-entries/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  hoursEntriesCreate,
  hoursEntriesUpdate,
  hoursEntriesTransition,
  hoursEntriesBulkApproveWorkerWeek,
  hoursEntriesBulkApproveWorkorderCraneWeek,
} from "../generated/sdk.gen.ts";
import type {
  HoursEntriesCreateData,
  HoursEntriesUpdateData,
  HoursEntriesTransitionData,
  HoursEntriesBulkApproveWorkerWeekData,
  HoursEntriesBulkApproveWorkorderCraneWeekData,
} from "../generated/types.gen.ts";
import { meKeys } from "../me/keys.ts";

type CreateBody = NonNullable<HoursEntriesCreateData["body"]>;
type UpdateBody = NonNullable<HoursEntriesUpdateData["body"]>;
type TransitionBody = NonNullable<HoursEntriesTransitionData["body"]>;
type BulkWorkerBody = NonNullable<HoursEntriesBulkApproveWorkerWeekData["body"]>;
type BulkWorkorderBody = NonNullable<HoursEntriesBulkApproveWorkorderCraneWeekData["body"]>;

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ["hours-entries"] });
}

export function useUpdateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdateBody }) => {
      const { data, error } = await hoursEntriesUpdate({ path: { id: vars.id }, body: vars.body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useTransitionEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: TransitionBody }) => {
      const { data, error } = await hoursEntriesTransition({
        path: { "id:transition": vars.id },
        body: vars.body,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useBulkApproveWorker() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: BulkWorkerBody) => {
      const { data, error } = await hoursEntriesBulkApproveWorkerWeek({ body: vars });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useBulkApproveWorkorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: BulkWorkorderBody) => {
      const { data, error } = await hoursEntriesBulkApproveWorkorderCraneWeek({ body: vars });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidate(qc),
  });
}

// Worker mutations

export function useCreateHoursEntry(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateBody) => {
      const { data, error } = await hoursEntriesCreate({ body });
      if (error) throw error;
      if (!data) throw new Error("Create failed");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: meKeys.all }),
  });
}

export function useUpdateHoursEntry(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdateBody }) => {
      const { data, error } = await hoursEntriesUpdate({
        path: { id: vars.id },
        body: vars.body,
      });
      if (error) throw error;
      if (!data) throw new Error("Update failed");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: meKeys.all }),
  });
}
